import { z } from 'zod';

/**
 * Wave 40 — Vitrine (house-sales showcase).
 *
 * Public-facing showcase for a single house project. Prospective buyers
 * browse construction progress, view curated photos and specs, and submit
 * a contact form. Admin approves leads and issues time-boxed access tokens
 * that unlock gated documents (plantas, alvará, memoriais, etc.).
 *
 * Architecture
 * ────────────
 * One house = one `projects` row (the operational project) + one `showcases`
 * row (the marketing shell). The showcase has its own slug, hero copy, public
 * sections, and a curated milestone timeline. All PUBLIC endpoints hit a
 * redacted API that never exposes costs, issues, RNCs, or internal members.
 *
 * Token lifecycle
 * ───────────────
 *   pending  → lead form submitted; awaits admin approval
 *   approved → admin issued a token; lead gets email with link
 *   rejected → admin declined; no token issued
 *   expired  → prospect token TTL elapsed
 *
 * Document access
 * ───────────────
 *   public   — visible to anyone with the showcase slug (no token needed)
 *   prospect — visible to approved leads only (prospect tier token required)
 *   buyer    — visible to buyers (buyer or delivery tier token required)
 */

// ───────────────────────────────── enums ─────────────────────────────────

/**
 * Showcase marketing status. A showcase moves from draft → published once
 * the PM completes the marketing content and is ready for public access.
 */
export const showcaseStatusSchema = z.enum([
  'draft',
  'published',
  'sold',
  'archived',
]);
export type ShowcaseStatus = z.infer<typeof showcaseStatusSchema>;

/**
 * Scrollytelling section types. Each kind maps to a Casa component that
 * renders differently: hero (splash), gallery (curated photos), specs (beds,
 * baths, parking), timeline (milestones), etc. The UI determines layout;
 * the schema is opaque and per-kind.
 */
export const showcaseSectionKindSchema = z.enum([
  'hero',
  'gallery',
  'explode',
  'progress_live',
  'timeline',
  'specs',
  'neighborhood',
  'floorplan',
  'quote',
  'cta',
  'docs_teaser',
]);
export type ShowcaseSectionKind = z.infer<typeof showcaseSectionKindSchema>;

/**
 * Token access tier. Prospect tokens are time-boxed (1-7d); buyer and
 * delivery tokens never expire. Delivery tier unlocks handoff ZIP.
 */
export const showcaseTokenTierSchema = z.enum([
  'prospect',
  'buyer',
  'delivery',
]);
export type ShowcaseTokenTier = z.infer<typeof showcaseTokenTierSchema>;

/**
 * Document kind — checklist category. PM curates which documents go into
 * the gated package, categorized by type (architectural drawing, legal
 * approvals, maintenance manual, etc.).
 */
export const showcaseDocumentKindSchema = z.enum([
  'matricula',
  'alvara_construcao',
  'habite_se',
  'projeto_arquitetonico',
  'projeto_estrutural',
  'memorial_descritivo',
  'art_rrt',
  'projeto_hidraulico',
  'projeto_eletrico',
  'prevencao_incendio',
  'sondagem_solo',
  'manual_proprietario',
  'vistoria_final',
  'plantas_customizadas',
  'fichas_acabamentos',
  'projeto_paisagistico',
  'fvs_consolidado',
  'rnc_encerrados',
  'garantias_fornecedores',
  'outro',
]);
export type ShowcaseDocumentKind = z.infer<typeof showcaseDocumentKindSchema>;

/**
 * Importance tier for a document. Mandatory items (matricula, alvará)
 * are required; recommended (project drawings) enhance credibility; optional
 * (warranties) nice-to-have. Drives the PM's document checklist colors
 * (red = missing mandatory, yellow = missing recommended, green = complete).
 */
export const showcaseDocumentTierSchema = z.enum([
  'mandatory',
  'recommended',
  'optional',
]);
export type ShowcaseDocumentTier = z.infer<typeof showcaseDocumentTierSchema>;

/**
 * Who can access a gated document. Public = anyone; prospect = lead with
 * valid prospect token; buyer = customer (buyer/delivery tier).
 */
export const showcaseDocumentAccessLevelSchema = z.enum([
  'public',
  'prospect',
  'buyer',
]);
export type ShowcaseDocumentAccessLevel = z.infer<
  typeof showcaseDocumentAccessLevelSchema
>;

/**
 * Lead approval workflow status. A prospect's contact form submission sits
 * in pending until the admin approves (and mints a token) or rejects.
 * Tokens expire after their TTL; leads marked rejected never get a token.
 */
export const showcaseLeadStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'expired',
]);
export type ShowcaseLeadStatus = z.infer<typeof showcaseLeadStatusSchema>;

// ──────────────────────────────── schemas ─────────────────────────────────

/**
 * Public-facing hero photo entry. Includes dimensions for responsive layout.
 */
export const showcaseHeroPhotoSchema = z.object({
  id: z.string(),
  url: z.string().url(),
  caption: z.string().nullable(),
  // Nullable because legacy photos may not have captured dimensions.
  // The client falls back to a CSS aspect ratio when unknown.
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});
export type ShowcaseHeroPhoto = z.infer<typeof showcaseHeroPhotoSchema>;

/**
 * Public-facing milestone (timeline entry). Describes a construction
 * milestone with optional completion date. isDelivery flags the final
 * handoff event.
 */
export const showcaseMilestonePublicSchema = z.object({
  label: z.string(),
  estimatedAt: z.string().nullable(), // YYYY-MM-DD
  completedAt: z.string().nullable(), // YYYY-MM-DD
  isDelivery: z.boolean(),
});
export type ShowcaseMilestonePublic = z.infer<
  typeof showcaseMilestonePublicSchema
>;

/**
 * Minimal public sneak-peek shape returned by GET /v1/public/showcase/<slug>.
 * Intentionally excludes cost breakdown, suppliers, issues, and any admin-only
 * fields. Includes hero photos, milestone timeline, and key listing details
 * so prospects can preview the house.
 */
export const publicShowcaseSchema = z.object({
  slug: z.string(),
  heroEyebrow: z.string().nullable(),
  heroHeadline: z.string().nullable(),
  heroSubheadline: z.string().nullable(),
  coverPhotoUrl: z.string().url().nullable(),
  neighborhood: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  bedrooms: z.number().int().nonnegative().nullable(),
  bathrooms: z.number().int().nonnegative().nullable(),
  parkingSpots: z.number().int().nonnegative().nullable(),
  areaSqm: z.number().int().positive().nullable(),
  lotSqm: z.number().int().positive().nullable(),
  amenities: z.array(z.string()),
  listingPriceCents: z.number().int().nonnegative().nullable(),
  estimatedDeliveryMonth: z.string().nullable(), // YYYY-MM
  progressPercent: z.number().int().min(0).max(100),
  currentStageLabel: z.string().nullable(),
  deliveryMonth: z.string().nullable(), // YYYY-MM if actually delivered
  heroPhotos: z.array(showcaseHeroPhotoSchema),
  milestones: z.array(showcaseMilestonePublicSchema),
  updatedAt: z.string(), // ISO 8601
  // Wave 78 F1 · SUB 1 — public lat/lng for the new interactive
  // MapaInterativo cena (replaces NinhoVerdeMap iframe). Backfilled by
  // the projects geocoding hook (migration 0250). Null when the
  // project hasn't been geocoded yet — front falls back to a soft
  // empty state instead of guessing.
  geo: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .nullable(),
  // Wave 78 F1 · SUB 3 — investment summary. Public-only; the JP
  // decision is "comprador vê 'a partir de R$ X' OU 'sob consulta';
  // banda P10-P90 fica gated atrás de token PM". Aqui só exibimos o
  // que NÃO é sigiloso: o listingPriceCents convertido em label
  // legível. PM-only fields ficam fora dessa DTO (Wave 78 F3).
  investmentSummary: z
    .object({
      label: z.string(), // "A partir de R$ 1.180.000" ou "Sob consulta"
      fromCents: z.number().int().nonnegative().nullable(),
    })
    .nullable(),
});
export type PublicShowcase = z.infer<typeof publicShowcaseSchema>;

/**
 * Admin-facing showcase row. Full internal state: project link, cover photo
 * ID, creator, timestamps. Used only by Builder admin.
 */
export const adminShowcaseSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  projectId: z.string(),
  slug: z.string(),
  status: showcaseStatusSchema,
  heroEyebrow: z.string().nullable(),
  heroHeadline: z.string().nullable(),
  heroSubheadline: z.string().nullable(),
  coverPhotoId: z.string().nullable(),
  neighborhood: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  bedrooms: z.number().int().nonnegative().nullable(),
  bathrooms: z.number().int().nonnegative().nullable(),
  parkingSpots: z.number().int().nonnegative().nullable(),
  areaSqm: z.number().int().positive().nullable(),
  lotSqm: z.number().int().positive().nullable(),
  amenities: z.array(z.string()),
  listingPriceCents: z.number().int().nonnegative().nullable(),
  estimatedDeliveryMonth: z.string().nullable(), // YYYY-MM
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
  ogImageUrl: z.string().url().nullable(),
  publishedAt: z.string().nullable(),
  soldAt: z.string().nullable(),
  // Post-sale owner transfer (migration 0310). When the PM transfers a
  // sold house to its buyer as proprietário, these link the showcase to
  // the owner-invitation that was minted. Null until transferred.
  transferredAt: z.string().nullable(),
  transferredLeadId: z.string().nullable(),
  transferredToEmail: z.string().nullable(),
  transferredToName: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminShowcase = z.infer<typeof adminShowcaseSchema>;

// ─────────────────────────── request schemas ──────────────────────────────

/**
 * Create a new showcase for a project. Project ID is mandatory; all
 * marketing fields default to empty/null.
 */
export const createShowcaseRequestSchema = z.object({
  projectId: z.string(),
  heroEyebrow: z.string().trim().max(120).optional(),
  heroHeadline: z.string().trim().max(240).optional(),
  heroSubheadline: z.string().trim().max(240).optional(),
  neighborhood: z.string().trim().max(120).optional(),
  city: z.string().trim().max(120).optional(),
  state: z.string().length(2).toUpperCase().optional(),
  bedrooms: z.number().int().nonnegative().optional(),
  bathrooms: z.number().int().nonnegative().optional(),
  parkingSpots: z.number().int().nonnegative().optional(),
  areaSqm: z.number().int().positive().optional(),
  lotSqm: z.number().int().positive().optional(),
  amenities: z.array(z.string().trim()).optional(),
  listingPriceCents: z.number().int().nonnegative().optional().nullable(),
  estimatedDeliveryMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Data deve ser YYYY-MM')
    .optional()
    .nullable(),
  seoTitle: z.string().trim().max(160).optional(),
  seoDescription: z.string().trim().max(320).optional(),
});
export type CreateShowcaseRequest = z.infer<typeof createShowcaseRequestSchema>;

/**
 * Patch a showcase. All fields optional; only supplied fields are updated.
 */
export const updateShowcaseRequestSchema = z
  .object({
    heroEyebrow: z.string().trim().max(120).nullable().optional(),
    heroHeadline: z.string().trim().max(240).nullable().optional(),
    heroSubheadline: z.string().trim().max(240).nullable().optional(),
    coverPhotoId: z.string().nullable().optional(),
    neighborhood: z.string().trim().max(120).nullable().optional(),
    city: z.string().trim().max(120).nullable().optional(),
    state: z.string().length(2).toUpperCase().nullable().optional(),
    bedrooms: z.number().int().nonnegative().nullable().optional(),
    bathrooms: z.number().int().nonnegative().nullable().optional(),
    parkingSpots: z.number().int().nonnegative().nullable().optional(),
    areaSqm: z.number().int().positive().nullable().optional(),
    lotSqm: z.number().int().positive().nullable().optional(),
    amenities: z.array(z.string().trim()).optional(),
    listingPriceCents: z.number().int().nonnegative().nullable().optional(),
    estimatedDeliveryMonth: z
      .string()
      .regex(/^\d{4}-\d{2}$/, 'Data deve ser YYYY-MM')
      .nullable()
      .optional(),
    seoTitle: z.string().trim().max(160).nullable().optional(),
    seoDescription: z.string().trim().max(320).nullable().optional(),
    ogImageUrl: z.string().url().nullable().optional(),
    status: showcaseStatusSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
  });
export type UpdateShowcaseRequest = z.infer<typeof updateShowcaseRequestSchema>;

/**
 * Create or update a scrollytelling section. Kind, position, and visibility
 * control what renders. Settings is per-kind opaque JSON (e.g., gallery
 * count, video URL, quote attribution).
 */
export const upsertShowcaseSectionRequestSchema = z.object({
  kind: showcaseSectionKindSchema,
  position: z.number().int().nonnegative(),
  visible: z.boolean().default(true),
  title: z.string().trim().max(240).optional(),
  bodyMd: z.string().trim().max(5000).optional(),
  mediaIds: z.array(z.string()).optional(),
  settings: z.record(z.unknown()).optional(),
});
export type UpsertShowcaseSectionRequest = z.infer<
  typeof upsertShowcaseSectionRequestSchema
>;

/**
 * Create or update a public milestone (timeline entry). labelPt is the
 * Portuguese label (always required); labelEn is the English translation
 * (optional). Icon is an emoji or lucide key. Stage ID links to the
 * internal werk stage for progress tracking.
 */
export const upsertShowcaseMilestoneRequestSchema = z.object({
  labelPt: z.string().trim().min(1).max(120),
  labelEn: z.string().trim().max(120).optional(),
  icon: z.string().trim().max(32).optional(),
  estimatedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
    .optional()
    .nullable(),
  completedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
    .optional()
    .nullable(),
  isDelivery: z.boolean().optional(),
  position: z.number().int().nonnegative(),
  stageId: z.string().nullable().optional(),
});
export type UpsertShowcaseMilestoneRequest = z.infer<
  typeof upsertShowcaseMilestoneRequestSchema
>;

/**
 * Approve a lead and issue an access token. TTL controls how long the
 * prospect token is valid (1–7 days, default 3). Label overrides the
 * token's human-readable name (defaults to lead.fullName).
 */
export const approveShowcaseLeadRequestSchema = z.object({
  ttlDays: z.number().int().min(1).max(14).default(3),
  label: z.string().trim().max(120).optional(),
});
export type ApproveShowcaseLeadRequest = z.infer<
  typeof approveShowcaseLeadRequestSchema
>;

/**
 * Upload a gated document (PDF or drawing). Metadata for PM admin UI:
 * kind, tier, access level, title, optional description, and file stats.
 */
export const uploadShowcaseDocumentRequestSchema = z.object({
  kind: showcaseDocumentKindSchema,
  tier: showcaseDocumentTierSchema,
  accessLevel: showcaseDocumentAccessLevelSchema,
  title: z.string().trim().min(1).max(240),
  description: z.string().trim().max(600).optional(),
  sizeBytes: z.number().int().positive(),
  mimeType: z.string().default('application/pdf'),
});
export type UploadShowcaseDocumentRequest = z.infer<
  typeof uploadShowcaseDocumentRequestSchema
>;

/**
 * Public lead form submission. Prospect fills name, email, phone (optional),
 * and message. Email is lowercased and validated. Phone matches international
 * format (optional, allows +55 11 99999-9999 style).
 */
export const createShowcaseLeadRequestSchema = z.object({
  fullName: z.string().trim().min(1).max(120),
  email: z.string().trim().email().toLowerCase(),
  phone: z
    .string()
    .regex(/^\+?[0-9 ()-]{7,20}$/, 'Telefone inválido')
    .optional()
    .nullable(),
  message: z.string().trim().max(500).optional().nullable(),
});
export type CreateShowcaseLeadRequest = z.infer<
  typeof createShowcaseLeadRequestSchema
>;

// ──────────────────────────── response schemas ────────────────────────────

/**
 * Admin-facing lead row. Includes source IP/user agent for audit.
 */
export const showcaseLeadSchema = z.object({
  id: z.string(),
  showcaseId: z.string(),
  fullName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  message: z.string().nullable(),
  sourceIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  status: showcaseLeadStatusSchema,
  approvedAt: z.string().nullable(),
  approvedByUserId: z.string().nullable(),
  tokenId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ShowcaseLead = z.infer<typeof showcaseLeadSchema>;

/**
 * Admin-facing token row (redacted). Includes TTL, usage tracking,
 * issuer, and lead linkage.
 */
export const showcaseTokenSchema = z.object({
  id: z.string(),
  showcaseId: z.string(),
  tier: showcaseTokenTierSchema,
  label: z.string().nullable(),
  email: z.string().nullable(),
  leadId: z.string().nullable(),
  expiresAt: z.string().nullable(),
  issuedAt: z.string(),
  revokedAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  usedCount: z.number().int().nonnegative(),
  issuedByUserId: z.string().nullable(),
  createdAt: z.string(),
});
export type ShowcaseToken = z.infer<typeof showcaseTokenSchema>;

/**
 * Admin-facing document metadata. Includes R2 key for download,
 * file size, MIME type, and access tier.
 */
export const showcaseDocumentSchema = z.object({
  id: z.string(),
  showcaseId: z.string(),
  kind: showcaseDocumentKindSchema,
  tier: showcaseDocumentTierSchema,
  accessLevel: showcaseDocumentAccessLevelSchema,
  title: z.string(),
  r2Key: z.string(),
  sizeBytes: z.number().int().positive(),
  mimeType: z.string(),
  description: z.string().nullable(),
  uploadedBy: z.string(),
  uploadedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type ShowcaseDocument = z.infer<typeof showcaseDocumentSchema>;

/**
 * Token-facing document response. Includes a signed downloadUrl instead of
 * R2 key (same data, different transport).
 */
export const gatedDocumentSchema = z.object({
  id: z.string(),
  kind: showcaseDocumentKindSchema,
  tier: showcaseDocumentTierSchema,
  title: z.string(),
  downloadUrl: z.string().url(),
  sizeBytes: z.number().int().positive(),
  mimeType: z.string(),
  description: z.string().nullable(),
});
export type GatedDocument = z.infer<typeof gatedDocumentSchema>;

/**
 * Single document checklist item. Drives the PM's document status rail:
 * shows which mandatory/recommended/optional docs are uploaded.
 */
export const showcaseDocumentChecklistItemSchema = z.object({
  tier: showcaseDocumentTierSchema,
  kind: showcaseDocumentKindSchema,
  title: z.string(),
  uploaded: z.boolean(),
  documentId: z.string().nullable(),
});
export type ShowcaseDocumentChecklistItem = z.infer<
  typeof showcaseDocumentChecklistItemSchema
>;

/**
 * Checklist summary. Computed counts for the PM dashboard:
 * red = has missing mandatory, yellow = has missing recommended,
 * green = all mandatory + recommended present.
 */
export const showcaseDocumentChecklistSummarySchema = z.object({
  status: z.enum(['red', 'yellow', 'green']),
  missingMandatory: z.number().int().nonnegative(),
  missingRecommended: z.number().int().nonnegative(),
});
export type ShowcaseDocumentChecklistSummary = z.infer<
  typeof showcaseDocumentChecklistSummarySchema
>;

/**
 * Full document checklist response: array of items + summary flags.
 */
export const showcaseDocumentChecklistSchema = z.object({
  items: z.array(showcaseDocumentChecklistItemSchema),
  summary: showcaseDocumentChecklistSummarySchema,
});
export type ShowcaseDocumentChecklist = z.infer<
  typeof showcaseDocumentChecklistSchema
>;

// ────────────────────────────── list responses ────────────────────────────

/**
 * List of leads for a showcase. Admin endpoint.
 */
export const listShowcaseLeadsResponseSchema = z.object({
  data: z.array(showcaseLeadSchema),
});
export type ListShowcaseLeadsResponse = z.infer<
  typeof listShowcaseLeadsResponseSchema
>;

